import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageLightbox } from '../../../components/ui/ImageLightbox';

interface Props {
  src: string | null;
  /** Card title — names the preview for screen readers and the lightbox. */
  title: string;
}

/** Card proportions (2.5 × 3.5), so the thumbnail crops nothing sideways. */
const THUMB = 'h-[88px] w-[63px] shrink-0 rounded object-cover';
const PREVIEW_WIDTH = 300;
const PREVIEW_HEIGHT = 420;
const GAP = 12;
const OPEN_DELAY_MS = 120;

/** Beside the thumbnail, flipped to its left when the right edge is too close,
 *  and always kept inside the viewport vertically — a row near the bottom of a
 *  198-card table must not push the preview off-screen. */
function previewPosition(rect: DOMRect) {
  const toTheRight = rect.right + GAP;
  const left =
    toTheRight + PREVIEW_WIDTH <= window.innerWidth
      ? toTheRight
      : Math.max(GAP, rect.left - GAP - PREVIEW_WIDTH);
  const centred = rect.top + rect.height / 2 - PREVIEW_HEIGHT / 2;
  const lowest = Math.max(GAP, window.innerHeight - PREVIEW_HEIGHT - GAP);
  return { left, top: Math.min(Math.max(GAP, centred), lowest) };
}

/**
 * The card image of a table row: big enough to recognise the card at a glance,
 * with the whole scan a hover away.
 *
 * Rendered as a button on purpose — both row components toggle their checkbox
 * from a click anywhere on the row and skip clicks that start inside a control
 * (`closest('input,button,a,label')`), so looking at a card never selects it.
 *
 * The hover preview is portalled to the body and positioned from the
 * thumbnail's own rect: inside the table it would be clipped by the drawer and
 * by the horizontal scroll container. No transform is involved anywhere — in
 * Tailwind 4 a translate utility composes with an existing transform instead of
 * replacing it, so positioning stays plain left/top.
 */
export function CardThumb({ src, title }: Props) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const timer = useRef<number | null>(null);
  const [preview, setPreview] = useState<{ left: number; top: number } | null>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  // The preview is placed from the thumbnail's rect at the moment it opens, so
  // a wheel scroll (which moves the row without moving the pointer) would leave
  // it hanging over the wrong place. Scrolling closes it instead. A layout
  // effect, so the listener is in place in the same commit that shows it.
  useLayoutEffect(() => {
    if (preview === null) return;
    const close = () => setPreview(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [preview]);

  if (!src) {
    return <div className={`${THUMB} bg-[var(--co-bg)]`} aria-hidden="true" />;
  }

  // A short delay, so dragging the pointer down the table does not flash a
  // preview over every row it crosses.
  const open = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) setPreview(previewPosition(rect));
    }, OPEN_DELAY_MS);
  };

  const close = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setPreview(null);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Preview ${title}`}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        onClick={() => {
          close();
          setZoomed(true);
        }}
        className="shrink-0 cursor-zoom-in rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--co-brand)]"
      >
        <img src={src} alt="" className={THUMB} />
      </button>

      {preview
        ? createPortal(
            <div
              data-testid="card-thumb-preview"
              aria-hidden="true"
              style={{
                left: preview.left,
                top: preview.top,
                width: PREVIEW_WIDTH,
                height: PREVIEW_HEIGHT,
              }}
              className="pointer-events-none fixed z-[55] rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] p-2 shadow-2xl"
            >
              <img src={src} alt="" className="h-full w-full object-contain" />
            </div>,
            document.body,
          )
        : null}

      {zoomed ? <ImageLightbox src={src} title={title} onClose={() => setZoomed(false)} /> : null}
    </>
  );
}
